/**
 * The quest lines (`docs/rpg.md` §13–14).
 *
 * §14 is a whole section about one sentence. "Solve 10 equations" is replaced
 * by a bridge that has fallen down, a wagon that carries 15 kg and a load of
 * 240 kg — the same arithmetic, given a reason. Every quest here is written to
 * that shape: somebody in the world has a problem, the mathematics is what
 * solves it, and the reward arrives because the bridge is mended rather than
 * because a counter reached ten.
 *
 * Four rules hold the file together, three of them enforced by
 * `validateQuests`:
 *
 *   - **The mathematics is the curriculum's.** A quest names concepts and
 *     lessons that already exist in `content/src/concepts/` and
 *     `content/src/lessons/`; it never asks a question of its own. Fantasy
 *     names, rigorous mathematics (§37) — the story is a reason to open a
 *     lesson, never a substitute for one.
 *   - **A kingdom's quests ask for that kingdom's mathematics.** A quest
 *     sending a student somewhere else belongs on the other kingdom's board.
 *   - **The main chain walks the regions in prerequisite order** — Counting
 *     Meadow before Sum Cottage before Times Table Grove — and ends on a quest
 *     that asks the student to *master* the kingdom's core concepts, which is
 *     the only objective kind that requires evidence rather than exposure.
 *   - **Two to four short paragraphs, and skippable.** A student who taps
 *     straight past every word must still be able to do the quest; the story is
 *     an invitation, not a comprehension exercise. Long story beats are a
 *     validation warning for exactly this reason.
 *
 * Rewards are modest and in one currency each (§31): 50–150 XP and 10–30 gold
 * for a main quest, less for a side quest. `XP_LESSON_COMPLETE` is 20, so the
 * whole of Starter Village's story pays about what twenty-eight lessons do —
 * enough to feel like progress, nowhere near enough to make the game level a
 * measure of how much story somebody has read (`docs/rpg-implementation-plan.md`
 * §0.2). No quest pays mastery, because mastery is measured and not given.
 *
 * The band is checked against the mathematics rather than against itself, which
 * is what stops the economy drifting as kingdoms are added. A kingdom's board
 * pays 340–500 XP for its chain and 95–115 for its side quests; all ten
 * kingdoms and all ten bosses come to about 7,900 XP against the 22,000-odd a
 * student earns reading the 266 lessons and practising each concept to the six
 * attempts mastery will not be claimed below — so the story is around a third
 * of the mathematics, and no single kingdom's story out-pays learning that
 * kingdom. `quests.test.ts` asserts both sums rather than trusting this
 * paragraph.
 *
 * The chain is shorter and cheaper where the kingdom is smaller: Trigonometry
 * Peaks holds ten concepts against Geometry's forty-four, and paying its four
 * quests what Geometry's five are paid would have made its story worth nearly
 * as much as learning the whole of it. Reward tracks how much story there is,
 * never how far up the map it sits.
 */

import type { QuestDef } from '@yuan-math/world';

/* -------------------------------------------------------------------------- */
/* Starter Village                                                            */
/* -------------------------------------------------------------------------- */

const VILLAGE = 'kingdom-starter-village';

const VILLAGE_MAIN: QuestDef[] = [
  {
    id: 'quest-village-lantern-count',
    kingdomId: VILLAGE,
    kind: 'main',
    order: 0,
    title: 'The Lantern Count',
    story: [
      { text: 'Dusk comes early under the meadow hills, and the lamplighter is one pair of hands short.' },
      {
        speaker: 'Mira the Lamplighter',
        text: 'Every lantern on the wall takes oil before dark. Miss one and the road out of the village goes black.',
      },
      { text: 'Nobody has counted the wall since the storm took part of it down. Count it, and the village keeps its light.' },
    ],
    objective: { kind: 'complete-lessons', lessonIds: ['counting-01'] },
    reward: { xp: 50, gold: 10 },
  },
  {
    id: 'quest-village-millers-ledger',
    kingdomId: VILLAGE,
    kind: 'main',
    order: 1,
    title: "The Miller's Ledger",
    story: [
      { text: 'Sacks in, sacks out, and a ledger that has not balanced since spring.' },
      {
        speaker: 'Odo the Miller',
        text: 'Four hundred and six sacks came in. I have given out two hundred and thirty-eight. The book says ninety are left. The barn disagrees, and the barn has never once been wrong.',
      },
      { text: 'Add the column, take the difference, and find out which of the two is lying.' },
    ],
    objective: { kind: 'practice', conceptIds: ['multi-digit-addition', 'multi-digit-subtraction'], correctCount: 6 },
    reward: { xp: 60, gold: 12 },
  },
  {
    id: 'quest-village-orchard-rows',
    kingdomId: VILLAGE,
    kind: 'main',
    order: 2,
    title: 'The Orchard Rows',
    story: [
      { text: 'The old orchard burned. The saplings arrived this morning, and nobody can agree how to plant them.' },
      {
        speaker: 'Bramble the Orchardist',
        text: 'Eight rows of seven, or seven rows of eight? The apprentices have been arguing since sunrise and the roots are drying out.',
      },
      { text: 'Settle it in the grove, where the trees are planted in rectangles and turning the field sideways changes nothing at all.' },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['multiplication-facts', 'multiplication-properties', 'multiply-by-tens'],
      count: 2,
    },
    reward: { xp: 80, gold: 15 },
  },
  {
    id: 'quest-village-harvest-share',
    kingdomId: VILLAGE,
    kind: 'main',
    order: 3,
    title: 'The Harvest Share',
    story: [
      { text: 'The harvest is in, and the barn has to be split between nine families before the frost.' },
      {
        speaker: 'Elder Hanne',
        text: 'Fair shares, and no shouting about what is left at the bottom — because there is always something left at the bottom.',
      },
      { text: 'What division actually asks for, what a remainder is, and how to say that one wagon holds three times what another does.' },
    ],
    objective: {
      kind: 'complete-lessons',
      lessonIds: ['division-meaning-01', 'division-remainders-01', 'multiplicative-comparison-01'],
    },
    reward: { xp: 90, gold: 18 },
  },
  {
    id: 'quest-village-road-out',
    kingdomId: VILLAGE,
    kind: 'main',
    order: 4,
    title: 'The Road Out of the Village',
    story: [
      { text: 'The gate stands open. The road east runs to the Number Kingdom, and it is four days of walking.' },
      {
        speaker: 'Elder Hanne',
        text: 'You will not meet a single sum out there. You will meet two, one behind the other, and nobody to check the first before you start the second.',
      },
      { text: 'Take the trail. Nobody leaves this village on one calculation.' },
    ],
    /*
     * The finale, and the only quest in the village that asks for mastery of
     * the kingdom's core: the two operations it is built on, the two-step
     * problems that need both, and the long division that needs everything.
     * Three of the four, not four of four — a single concept a student happens
     * not to have met should not hold the road shut (§0.6), the same
     * four-in-five forgiveness the kingdom seals are written with.
     */
    objective: {
      kind: 'master-concepts',
      conceptIds: ['multiplication-facts', 'division-meaning', 'two-step-problems', 'long-division'],
      count: 3,
    },
    reward: { xp: 150, gold: 30 },
  },
];

const VILLAGE_SIDE: QuestDef[] = [
  {
    id: 'quest-village-sparrow-errand',
    kingdomId: VILLAGE,
    kind: 'review',
    // Side quests sit past the end of the chain on the board. They are never
    // locked by it — `questProgress` locks main quests and nothing else.
    order: 100,
    title: "The Sparrow's Errand",
    story: [
      { text: 'The message sparrows are counted, fed and sent twice a day, and the count is the part everyone gets wrong.' },
      {
        speaker: 'Wren the Sparrowkeeper',
        text: 'It is not hard. It is only never finished. Come back tomorrow and the whole lot wants doing again.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['counting', 'multi-digit-addition'], correctCount: 6 },
    reward: { xp: 30, gold: 6 },
  },
  {
    id: 'quest-village-bakers-dozen',
    kingdomId: VILLAGE,
    kind: 'side',
    order: 101,
    title: "The Baker's Dozen Dispute",
    story: [
      { text: 'Two bakers, one market stall, and an argument loud enough to hear from the meadow.' },
      {
        speaker: 'Piet the Baker',
        text: 'She says her basket holds three times mine. I say it holds mine and eleven over. We cannot both be right, and the bread is going cold.',
      },
      { text: 'A remainder and a comparison settle it, and the stall opens before noon.' },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['division-remainders', 'multiplicative-comparison'],
      count: 1,
    },
    reward: { xp: 40, gold: 8 },
  },
  {
    id: 'quest-village-midsummer-fete',
    kingdomId: VILLAGE,
    kind: 'side',
    order: 102,
    title: 'The Midsummer Fête',
    story: [
      { text: 'Midsummer, and the village wants a reason to light the long tables.' },
      {
        speaker: 'Elder Hanne',
        text: 'You mended the ledger, settled the orchard, split the harvest and walked the trail. That is reason enough. Sit down and eat.',
      },
    ],
    objective: { kind: 'clear-quests', questIds: VILLAGE_MAIN.map((quest) => quest.id) },
    reward: { xp: 45, gold: 9 },
  },
];

/* -------------------------------------------------------------------------- */
/* Number Kingdom                                                             */
/* -------------------------------------------------------------------------- */

const NUMBER = 'kingdom-number';

const NUMBER_MAIN: QuestDef[] = [
  {
    id: 'quest-number-granary-tally',
    kingdomId: NUMBER,
    kind: 'main',
    order: 0,
    title: 'The Granary Tally',
    story: [
      { text: 'The Number Kingdom counts everything it owns, and writes almost none of it in columns.' },
      {
        speaker: 'Tallymaster Vell',
        text: 'Four thousand and six, or four hundred and sixty? A scribe left the empty column out and the granary has been wrong for a year.',
      },
      { text: 'Put the digits back where their columns say they belong — and learn when a rounded number is the more honest one.' },
    ],
    objective: { kind: 'complete-lessons', lessonIds: ['place-value-01', 'rounding-01'] },
    reward: { xp: 50, gold: 10 },
  },
  {
    id: 'quest-number-harbourmaster-toll',
    kingdomId: NUMBER,
    kind: 'main',
    order: 1,
    title: "The Harbourmaster's Toll",
    story: [
      { text: 'Decimal Docks charges by the hundredth of a coin, and the queue is down the pier.' },
      {
        speaker: 'Harbourmaster Sella',
        text: 'Point four or point four zero — half the captains swear those are different tolls. Settle that, then add up a day of takings without losing the point.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['compare-decimals', 'add-decimals'], correctCount: 8 },
    reward: { xp: 70, gold: 14 },
  },
  {
    id: 'quest-number-below-the-waterline',
    kingdomId: NUMBER,
    kind: 'main',
    order: 2,
    title: 'Below the Waterline',
    story: [
      { text: 'The salt mine runs under the harbour. The surface is zero, and everything worth having is below it.' },
      {
        speaker: 'Foreman Kesk',
        text: 'Gallery minus forty and gallery plus forty are the same walk from the lift. Tell the winch crew, who send the rope the wrong way twice a week.',
      },
      { text: 'Negatives, opposites, and how far a number sits from zero whichever side of it the number is on.' },
    ],
    objective: { kind: 'master-concepts', conceptIds: ['integers', 'absolute-value-opposites'], count: 2 },
    reward: { xp: 90, gold: 18 },
  },
  {
    id: 'quest-number-foundry-recipe',
    kingdomId: NUMBER,
    kind: 'main',
    order: 3,
    title: 'The Foundry Recipe',
    story: [
      { text: 'The Prime Foundry melts a number down to find out what it was built from.' },
      {
        speaker: 'Smith Ordelia',
        text: 'Two smiths, one number, two recipes. If they ever disagreed the Foundry would be out of a trade — and in nine hundred years they never have. Find out why.',
      },
    ],
    objective: {
      kind: 'complete-lessons',
      lessonIds: ['prime-composite-01', 'prime-factorization-01', 'gcf-lcm-01'],
    },
    reward: { xp: 100, gold: 20 },
  },
  {
    id: 'quest-number-keystone',
    kingdomId: NUMBER,
    kind: 'main',
    order: 4,
    title: 'The Keystone of the Estuary',
    story: [
      { text: 'The estuary bridge has dropped its keystone, and the mason will not cut a new one from a guess.' },
      {
        speaker: 'Mason Aldric',
        text: 'Two hundred and forty kilos of stone to bring up. Each cart carries fifteen. I want the number of trips before I want anything else, and I want whoever tells me it to be certain.',
      },
      { text: 'Every road out of the kingdom crosses that bridge. The arithmetic is not the hard part; being sure of it is.' },
    ],
    /*
     * §14's broken bridge, kept as the finale it deserves to be. The objective
     * is mastery of the kingdom's core — one concept from each of the four
     * regions the chain has walked — because "I am certain" is precisely the
     * claim mastery makes and exposure does not.
     */
    objective: {
      kind: 'master-concepts',
      conceptIds: ['place-value', 'decimal-place-value', 'integers', 'prime-factorization'],
      count: 3,
    },
    reward: { xp: 150, gold: 30 },
  },
];

const NUMBER_SIDE: QuestDef[] = [
  {
    id: 'quest-number-tidewatchers-rounds',
    kingdomId: NUMBER,
    kind: 'review',
    order: 100,
    title: "The Tidewatcher's Rounds",
    story: [
      { text: 'The tide tables are copied out by hand every morning, and one slipped column floods a warehouse.' },
      {
        speaker: 'Tidewatcher Bo',
        text: 'Columns first, then round it so a captain can read the thing from the deck. Same rounds tomorrow, and the day after.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['place-value', 'rounding'], correctCount: 6 },
    reward: { xp: 30, gold: 6 },
  },
  {
    id: 'quest-number-assayers-commission',
    kingdomId: NUMBER,
    kind: 'side',
    order: 101,
    title: "The Assayer's Commission",
    story: [
      { text: 'The assayer weighs ore to the thousandth of a measure and is paid by how little she is wrong.' },
      {
        speaker: 'Assayer Runa',
        text: 'Multiply two decimals and the point will not stay where you left it. Divide by one and it moves again. Get that wrong here and somebody is robbed.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['multiply-decimals', 'divide-decimals'], correctCount: 8 },
    reward: { xp: 40, gold: 8 },
  },
  {
    id: 'quest-number-charter-of-ten',
    kingdomId: NUMBER,
    kind: 'side',
    order: 102,
    title: 'The Charter of Ten',
    story: [
      { text: 'A kingdom built on ten columns keeps its charter in the counting house, and adds a name to it now and then.' },
      {
        speaker: 'Tallymaster Vell',
        text: 'Granary, docks, mine, foundry, bridge. You have been round the whole of it. Sign.',
      },
    ],
    objective: { kind: 'clear-quests', questIds: NUMBER_MAIN.map((quest) => quest.id) },
    reward: { xp: 45, gold: 9 },
  },
];

/* -------------------------------------------------------------------------- */
/* Fraction Kingdom                                                           */
/* -------------------------------------------------------------------------- */

const FRACTION = 'kingdom-fraction';

const FRACTION_MAIN: QuestDef[] = [
  {
    id: 'quest-fraction-a-half-of-what',
    kingdomId: FRACTION,
    kind: 'main',
    order: 0,
    title: 'A Half of What?',
    story: [
      { text: 'Slice Village cuts everything it owns into equal parts, and has spent the week arguing about one word.' },
      {
        speaker: 'Chef Sorrel',
        text: 'Half a tray from the small oven and half a tray from the big one. The carters have written that down as one tray. It is not one tray, and I would like the reason in writing before market day.',
      },
      { text: 'A fraction is two facts and the village keeps losing the second: how many parts, and how big the whole was.' },
    ],
    objective: { kind: 'complete-lessons', lessonIds: ['fraction-basics-01', 'fractions-on-number-line-01'] },
    reward: { xp: 50, gold: 10 },
  },
  {
    id: 'quest-fraction-mill-of-other-names',
    kingdomId: FRACTION,
    kind: 'main',
    order: 1,
    title: 'The Mill of Other Names',
    story: [
      { text: 'The Equivalence Mill grinds a fraction until it comes back out with a different name and exactly the same value.' },
      {
        speaker: 'Chef Sorrel',
        text: 'Three eighths against two fifths. The mill will tell you which is the larger. The point of the exercise is that you should be able to tell me first.',
      },
      { text: 'Grind them both to a name they share and the comparison stops being an opinion.' },
    ],
    objective: { kind: 'practice', conceptIds: ['equivalent-fractions', 'compare-fractions'], correctCount: 6 },
    reward: { xp: 70, gold: 14 },
  },
  {
    id: 'quest-fraction-two-kettles',
    kingdomId: FRACTION,
    kind: 'main',
    order: 2,
    title: 'Two Kettles, One Pot',
    story: [
      { text: 'The Mixing Kitchens have taken an order for a wedding, and the recipe is written in three different fractions of a batch.' },
      {
        speaker: 'Chef Sorrel',
        text: 'Three quarters of one pot and two thirds of another, into a pot that holds one and a half. Tell me whether it fits before I pour it, not after.',
      },
      { text: 'Parts only add when the parts are the same size — and a mixed number is very good at hiding that they are not.' },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['add-like-fractions', 'add-fractions', 'add-mixed-numbers'],
      count: 2,
    },
    reward: { xp: 90, gold: 18 },
  },
  {
    id: 'quest-fraction-where-dividing-makes-more',
    kingdomId: FRACTION,
    kind: 'main',
    order: 3,
    title: 'Where Dividing Makes More',
    story: [
      { text: "Divider's Hollow is where the ribbon is cut, and where every apprentice gets the same shock in their first week." },
      {
        speaker: 'Tam of the Hollow',
        text: 'Six metres of ribbon, cut into lengths of a third of a metre. Everyone guesses two. It is eighteen, and I have given up explaining that at the market stall.',
      },
      { text: 'Dividing by something smaller than one makes the answer bigger. Watch it happen once rather than memorising the rule.' },
    ],
    objective: {
      kind: 'complete-lessons',
      lessonIds: ['fraction-as-division-01', 'divide-unit-fractions-01', 'divide-fractions-01'],
    },
    reward: { xp: 100, gold: 20 },
  },
  {
    id: 'quest-fraction-price-of-a-third',
    kingdomId: FRACTION,
    kind: 'main',
    order: 4,
    title: 'The Price of a Third',
    story: [
      { text: 'Market day. Every stall on the Ratio Market prices by comparison — three for a coin, sixty per cent off, two parts flour to one of water — and no two of them the same way.' },
      {
        speaker: 'Chef Sorrel',
        text: 'You can cut a cake. Now go and buy the flour for forty of them without being robbed. It is the same mathematics in a coat, and the market knows it even if you do not.',
      },
      { text: 'The whole kingdom, from the first slice to the last percentage, and the only proof the market accepts is doing it.' },
    ],
    /*
     * The finale, and mastery rather than exposure: four of six, one from each
     * end of the kingdom. A student who never met mixed numbers should still be
     * able to close the story (§0.6), which is why the count is not six.
     */
    objective: {
      kind: 'master-concepts',
      conceptIds: [
        'compare-fractions',
        'add-fractions',
        'multiply-fractions',
        'divide-fractions',
        'ratios',
        'percentages',
      ],
      count: 4,
    },
    reward: { xp: 150, gold: 30 },
  },
];

const FRACTION_SIDE: QuestDef[] = [
  {
    id: 'quest-fraction-morning-weigh-in',
    kingdomId: FRACTION,
    kind: 'review',
    order: 100,
    title: "Sorrel's Morning Weigh-In",
    story: [
      { text: 'The kitchen weighs out the same eight things every morning, and every morning somebody writes four eighths where they meant a half.' },
      {
        speaker: 'Chef Sorrel',
        text: 'It is not difficult. It is only every single day, and the day it goes wrong will be the day nobody thought it was worth checking.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['fraction-basics', 'equivalent-fractions'], correctCount: 6 },
    reward: { xp: 30, gold: 6 },
  },
  {
    id: 'quest-fraction-doubled-recipe',
    kingdomId: FRACTION,
    kind: 'side',
    order: 101,
    title: 'The Doubled Recipe',
    story: [
      { text: 'A banquet for twice the guests, and the only copy of the recipe is written for a single tray.' },
      {
        speaker: 'Chef Sorrel',
        text: 'Multiply by two and everything grows. Multiply by three quarters and it shrinks — the same operation, and half my kitchen still finds that suspicious.',
      },
    ],
    objective: { kind: 'master-concepts', conceptIds: ['fraction-times-whole', 'fraction-scaling'], count: 1 },
    reward: { xp: 40, gold: 8 },
  },
  {
    id: 'quest-fraction-feast-of-equal-parts',
    kingdomId: FRACTION,
    kind: 'side',
    order: 102,
    title: 'The Feast of Equal Parts',
    story: [
      { text: 'The long tables run the length of Slice Village, and everything laid on them has been divided by somebody who now knows how.' },
      {
        speaker: 'Chef Sorrel',
        text: 'Sit down and eat. And do not tell me what fraction of the pie you have taken — tonight I would rather not know.',
      },
    ],
    objective: { kind: 'clear-quests', questIds: FRACTION_MAIN.map((quest) => quest.id) },
    reward: { xp: 45, gold: 9 },
  },
];

/* -------------------------------------------------------------------------- */
/* Algebra Kingdom                                                            */
/* -------------------------------------------------------------------------- */

const ALGEBRA = 'kingdom-algebra';

const ALGEBRA_MAIN: QuestDef[] = [
  {
    id: 'quest-algebra-letter-on-the-door',
    kingdomId: ALGEBRA,
    kind: 'main',
    order: 0,
    title: 'The Letter on the Door',
    story: [
      { text: 'Every house in Variable Village has a letter on the door instead of a number, and somehow the post still arrives.' },
      {
        speaker: 'Professor Ada',
        text: 'x is not a mystery and it is not a code. It is a box with a number in it that we have not opened yet. Write down what the box does and opening it becomes ordinary arithmetic.',
      },
      { text: 'Write a rule with a letter in it, and read one somebody else wrote without guessing at what they meant.' },
    ],
    objective: { kind: 'complete-lessons', lessonIds: ['variables-01', 'algebraic-expressions-01'] },
    reward: { xp: 50, gold: 10 },
  },
  {
    id: 'quest-algebra-equation-gate',
    kingdomId: ALGEBRA,
    kind: 'main',
    order: 1,
    title: 'The Equation Gate',
    story: [
      { text: 'The old gate into Equation Forest has been shut since the last keeper died, and the mechanism turns out to be a pair of scales.' },
      {
        speaker: 'Professor Ada',
        text: 'Welcome to the Algebra Kingdom. Our ancient Equation Gate is locked, and it opens for anyone who can do the same thing to both sides without flinching.',
      },
      { text: 'One step, and then several. The paths beyond only hold if you kept them level on the way in.' },
    ],
    objective: { kind: 'practice', conceptIds: ['one-step-equations', 'multi-step-equations'], correctCount: 6 },
    reward: { xp: 70, gold: 14 },
  },
  {
    id: 'quest-algebra-marsh-is-never-level',
    kingdomId: ALGEBRA,
    kind: 'main',
    order: 2,
    title: 'The Marsh Is Never Level',
    story: [
      { text: 'Inequality Marsh has no dry line, only stretches that are dry enough, and the ferry runs whenever the water sits below a mark on the post.' },
      {
        speaker: 'Professor Ada',
        text: 'The answer here is not a point. It is everything on one side of a point — and multiply through by a negative and the whole stretch turns round. Forget that once and the ferry goes out in a flood.',
      },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['one-variable-inequalities', 'compound-inequalities', 'absolute-value'],
      count: 2,
    },
    reward: { xp: 90, gold: 18 },
  },
  {
    id: 'quest-algebra-folding-the-long-field',
    kingdomId: ALGEBRA,
    kind: 'main',
    order: 3,
    title: 'Folding the Long Field',
    story: [
      { text: 'Polynomial Plains are surveyed in strips, and the surveyors would far rather have two short factors than one long expression.' },
      {
        speaker: 'Professor Ada',
        text: 'x² + 5x + 6 and (x + 2)(x + 3) are the same field. Only one of them tells you where to put the fences.',
      },
      { text: 'Fold it, then unfold it again to check. Both directions, every time, or the fence goes in the wrong place.' },
    ],
    objective: {
      kind: 'complete-lessons',
      lessonIds: ['polynomials-01', 'factoring-01', 'polynomial-division-01'],
    },
    reward: { xp: 100, gold: 20 },
  },
  {
    id: 'quest-algebra-both-doors',
    kingdomId: ALGEBRA,
    kind: 'main',
    order: 4,
    title: 'Both Doors of the Castle',
    story: [
      { text: 'Quadratic Castle has two doors and one formula that opens both, however unpleasant the numbers behind them turn out to be.' },
      {
        speaker: 'Professor Ada',
        text: 'Two answers is not a mistake. A parabola crosses twice, and a student who reports one of them has stopped reading halfway through their own working.',
      },
      { text: 'Everything the kingdom taught at once: an expression, an equation, a factoring, and the formula that never fails.' },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['linear-expressions', 'multi-step-equations', 'factoring', 'quadratics', 'quadratic-formula'],
      count: 3,
    },
    reward: { xp: 150, gold: 30 },
  },
];

const ALGEBRA_SIDE: QuestDef[] = [
  {
    id: 'quest-algebra-chalk-round',
    kingdomId: ALGEBRA,
    kind: 'review',
    order: 100,
    title: "Ada's Chalk Round",
    story: [
      { text: 'Professor Ada takes the first-year class at seven in the morning, and it is the same board every single week.' },
      {
        speaker: 'Professor Ada',
        text: 'Substitute, then solve. It never becomes interesting, and it never once stops mattering. That is why we do it before breakfast.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['variables', 'one-step-equations'], correctCount: 6 },
    reward: { xp: 30, gold: 6 },
  },
  {
    id: 'quest-algebra-rearranged-formula',
    kingdomId: ALGEBRA,
    kind: 'side',
    order: 101,
    title: 'The Rearranged Formula',
    story: [
      { text: "The kingdom's engineers keep every formula in the wrong arrangement for whatever job is actually in front of them." },
      {
        speaker: 'Professor Ada',
        text: 'They have the area and they want the height. Rearranging is solving, with letters standing where the numbers are going to be.',
      },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['literal-equations', 'proportional-relationships'],
      count: 1,
    },
    reward: { xp: 40, gold: 8 },
  },
  {
    id: 'quest-algebra-charter-of-the-balance',
    kingdomId: ALGEBRA,
    kind: 'side',
    order: 102,
    title: 'The Charter of the Balance',
    story: [
      { text: 'The village, the forest, the marsh, the plains and the castle. The Charter is signed back at the gate you unlocked first.' },
      {
        speaker: 'Professor Ada',
        text: 'The same thing to both sides, every time. That is the whole of this kingdom and you may write it on one line.',
      },
    ],
    objective: { kind: 'clear-quests', questIds: ALGEBRA_MAIN.map((quest) => quest.id) },
    reward: { xp: 45, gold: 9 },
  },
];

/* -------------------------------------------------------------------------- */
/* Geometry Kingdom                                                           */
/* -------------------------------------------------------------------------- */

const GEOMETRY = 'kingdom-geometry';

const GEOMETRY_MAIN: QuestDef[] = [
  {
    id: 'quest-geometry-rope-and-rod',
    kingdomId: GEOMETRY,
    kind: 'main',
    order: 0,
    title: 'The Rope and the Rod',
    story: [
      { text: 'The Geometry Kingdom is re-surveying itself, and the old measurements are recorded in four units nobody alive still uses.' },
      {
        speaker: 'Surveyor Idris',
        text: 'Perimeter is how much fence. Area is how much grass. Half the disputes in this kingdom are somebody charging for the one and delivering the other.',
      },
    ],
    objective: {
      kind: 'complete-lessons',
      lessonIds: ['unit-conversion-01', 'perimeter-01', 'area-rectangles-01'],
    },
    reward: { xp: 50, gold: 10 },
  },
  {
    id: 'quest-geometry-what-counts-as-a-roof',
    kingdomId: GEOMETRY,
    kind: 'main',
    order: 1,
    title: 'What Counts as a Roof',
    story: [
      { text: "Shape Hamlet's roofers are paid by the figure, and which figure is which has been argued about since the hamlet was founded." },
      {
        speaker: 'Surveyor Idris',
        text: 'A square is a rectangle. A rectangle is a parallelogram. Say either out loud in the tavern and then stand well back.',
      },
      { text: 'Name a figure by the properties it has, not by how it happens to sit when you come up the road.' },
    ],
    objective: { kind: 'practice', conceptIds: ['classify-quadrilaterals', 'polygons'], correctCount: 6 },
    reward: { xp: 70, gold: 14 },
  },
  {
    id: 'quest-geometry-nothing-believed',
    kingdomId: GEOMETRY,
    kind: 'main',
    order: 2,
    title: 'Nothing Believed Until Argued',
    story: [
      { text: 'Proof Ridge is the high road, and nothing enters the kingdom record without a reason written beside it.' },
      {
        speaker: 'Surveyor Idris',
        text: 'Two roof beams cross a rafter and you tell me those two angles are equal. I do not doubt you. I am asking why, and "it looks like it" has collapsed three barns.',
      },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['angle-relationships', 'parallel-lines', 'triangle-congruence', 'pythagoras'],
      count: 2,
    },
    reward: { xp: 90, gold: 18 },
  },
  {
    id: 'quest-geometry-one-point-one-distance',
    kingdomId: GEOMETRY,
    kind: 'main',
    order: 3,
    title: 'One Point, One Distance',
    story: [
      { text: 'The Circle Sanctum was laid out from a single peg and a length of rope, and every theorem inside it follows from those two things.' },
      {
        speaker: 'Surveyor Idris',
        text: 'The mason wants the length of the arch and the area of the window. Both come off the same circle, and neither of them is going to be a guess.',
      },
    ],
    objective: {
      kind: 'complete-lessons',
      lessonIds: ['circles-01', 'circle-theorems-01', 'arc-sector-01'],
    },
    reward: { xp: 100, gold: 20 },
  },
  {
    id: 'quest-geometry-survey-of-the-coast',
    kingdomId: GEOMETRY,
    kind: 'main',
    order: 4,
    title: 'The Survey of the Coast',
    story: [
      { text: 'The coast has never been mapped, on the grounds that it will not hold still long enough to be paced out.' },
      {
        speaker: 'Surveyor Idris',
        text: 'Give every corner an address and the whole thing turns into arithmetic. Distance, area, angle, circle — all of it, off numbers on a grid, from a desk, in the dry.',
      },
      { text: 'The last survey of the kingdom, and it needs every part of the kingdom to finish.' },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['area-rectangles', 'polygons', 'pythagoras', 'circles', 'coordinate-distance'],
      count: 3,
    },
    reward: { xp: 150, gold: 30 },
  },
];

const GEOMETRY_SIDE: QuestDef[] = [
  {
    id: 'quest-geometry-morning-round',
    kingdomId: GEOMETRY,
    kind: 'review',
    order: 100,
    title: "Idris's Morning Round",
    story: [
      { text: 'The kingdom is measured again every spring, because over a winter every fence in it has moved a little.' },
      {
        speaker: 'Surveyor Idris',
        text: 'Walk the boundary, name the shapes, write it down. Then come back next spring and do exactly the same thing.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['perimeter', 'shape-attributes'], correctCount: 6 },
    reward: { xp: 30, gold: 6 },
  },
  {
    id: 'quest-geometry-cathedral-windows',
    kingdomId: GEOMETRY,
    kind: 'side',
    order: 101,
    title: 'The Cathedral Windows',
    story: [
      { text: "The cathedral's rose window is one pane of glass, drawn once and then turned fifteen times." },
      {
        speaker: 'Surveyor Idris',
        text: 'Reflect it, rotate it, slide it. Cut one piece of glass instead of sixteen and the guild will be calling it cheating for the next hundred years.',
      },
    ],
    objective: { kind: 'master-concepts', conceptIds: ['symmetry', 'transformations'], count: 1 },
    reward: { xp: 40, gold: 8 },
  },
  {
    id: 'quest-geometry-map-that-closes',
    kingdomId: GEOMETRY,
    kind: 'side',
    order: 102,
    title: 'The Map That Closes',
    story: [
      { text: 'A map closes when you can walk its boundary from the vale to the coast and arrive back at the peg you started from.' },
      {
        speaker: 'Surveyor Idris',
        text: 'It closes. First time in ninety years, and not by half a rope-length either. Sign it, and date it, and I will have it copied.',
      },
    ],
    objective: { kind: 'clear-quests', questIds: GEOMETRY_MAIN.map((quest) => quest.id) },
    reward: { xp: 45, gold: 9 },
  },
];

/* -------------------------------------------------------------------------- */
/* Function Kingdom                                                           */
/* -------------------------------------------------------------------------- */

/*
 * Three regions rather than five, so the chain is four quests rather than five:
 * the rule is that the chain walks the kingdom, not that it hits a quota. Two
 * quests sit in the Foundry because the Foundry holds thirteen concepts and the
 * Spiral holds three.
 */
const FUNCTION = 'kingdom-function';

const FUNCTION_MAIN: QuestDef[] = [
  {
    id: 'quest-function-one-in-one-out',
    kingdomId: FUNCTION,
    kind: 'main',
    order: 0,
    title: 'One In, One Out',
    story: [
      { text: 'The Function Foundry runs on machines that take one number and hand back exactly one other, and the floor supervisor is severe about the word "exactly".' },
      {
        speaker: 'Engineer Halla',
        text: 'Feed it a three and it gives you a seven. Feed it a three tomorrow and it had better still give you a seven. A machine with two answers is scrap metal with a handle on it.',
      },
    ],
    objective: { kind: 'complete-lessons', lessonIds: ['functions-intro-01', 'linear-functions-01'] },
    reward: { xp: 50, gold: 10 },
  },
  {
    id: 'quest-function-machine-room-floor',
    kingdomId: FUNCTION,
    kind: 'main',
    order: 1,
    title: 'The Machine Room Floor',
    story: [
      { text: 'Every machine on the floor is the same machine, moved: shifted up, stretched, flipped over, or run backwards.' },
      {
        speaker: 'Engineer Halla',
        text: 'We do not build a new one for every job. We build one and we move it. Learn where it was moved to and you can read the whole floor from the doorway.',
      },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['function-transformations', 'inverse-functions', 'quadratic-graphs'],
      count: 2,
    },
    reward: { xp: 80, gold: 16 },
  },
  {
    id: 'quest-function-light-that-outruns',
    kingdomId: FUNCTION,
    kind: 'main',
    order: 2,
    title: 'The Light That Outruns the Line',
    story: [
      { text: 'The Logarithm Lighthouse is tracking something that doubles, and the chart ran off the top of the page on the fourth day.' },
      {
        speaker: 'Engineer Halla',
        text: 'Nothing straight catches it. The lamp is the only instrument on this coast that reads growth like that back down to a size a person can hold in their hand.',
      },
    ],
    objective: {
      kind: 'complete-lessons',
      lessonIds: ['exponential-functions-01', 'logarithms-01', 'exponential-equations-01'],
    },
    reward: { xp: 110, gold: 22 },
  },
  {
    id: 'quest-function-stair-with-no-last-step',
    kingdomId: FUNCTION,
    kind: 'main',
    order: 3,
    title: 'The Stair With No Last Step',
    story: [
      { text: 'The Sequence Spiral is a staircase still under construction, built one step at a time by standing on the step below it.' },
      {
        speaker: 'Engineer Halla',
        text: 'Show me it holds for the first step, and that every step carries the next one. Then it holds all the way up, and neither of us has to climb it to find out.',
      },
      { text: 'The whole foundry in one shift: a machine, a graph, a lamp and a staircase.' },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: [
        'functions-intro',
        'linear-functions',
        'exponential-functions',
        'logarithms',
        'sequences',
        'series',
      ],
      count: 4,
    },
    reward: { xp: 150, gold: 30 },
  },
];

const FUNCTION_SIDE: QuestDef[] = [
  {
    id: 'quest-function-test-bench',
    kingdomId: FUNCTION,
    kind: 'review',
    order: 100,
    title: "Halla's Test Bench",
    story: [
      { text: 'Every machine is bench-tested before it leaves the foundry, with the same inputs, in the same order, by whoever is nearest.' },
      {
        speaker: 'Engineer Halla',
        text: 'What goes in, what comes out, and does it ever hand back two. That is the whole test, and it has never once been a waste of an afternoon.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['functions-intro', 'linear-functions'], correctCount: 6 },
    reward: { xp: 30, gold: 6 },
  },
  {
    id: 'quest-function-ladder-proof',
    kingdomId: FUNCTION,
    kind: 'side',
    order: 101,
    title: 'The Ladder Proof',
    story: [
      { text: 'A ladder is being fitted to the spiral, and the guild will not certify a ladder on the strength of somebody testing the first four rungs.' },
      {
        speaker: 'Engineer Halla',
        text: 'One rung, and every rung holding the next. That certifies all of them at once, including the ones nobody has built yet.',
      },
    ],
    objective: { kind: 'master-concepts', conceptIds: ['mathematical-induction', 'sequences'], count: 1 },
    reward: { xp: 40, gold: 8 },
  },
  {
    id: 'quest-function-foundry-ledger',
    kingdomId: FUNCTION,
    kind: 'side',
    order: 102,
    title: 'The Foundry Ledger',
    story: [
      { text: 'The ledger lists every machine commissioned this season, what it does, and who put their name under it.' },
      {
        speaker: 'Engineer Halla',
        text: 'Floor, lighthouse, spiral. Put your name at the bottom of the page — it is your foundry as much as mine now.',
      },
    ],
    objective: { kind: 'clear-quests', questIds: FUNCTION_MAIN.map((quest) => quest.id) },
    reward: { xp: 45, gold: 9 },
  },
];

/* -------------------------------------------------------------------------- */
/* Trigonometry Peaks                                                         */
/* -------------------------------------------------------------------------- */

const TRIGONOMETRY = 'kingdom-trigonometry';

const TRIGONOMETRY_MAIN: QuestDef[] = [
  {
    id: 'quest-trig-height-of-the-north-face',
    kingdomId: TRIGONOMETRY,
    kind: 'main',
    order: 0,
    title: 'The Height of the North Face',
    story: [
      { text: 'Nobody has ever measured the north face, because measuring it has always meant climbing it, and climbing it means knowing first how high it is.' },
      {
        speaker: 'Guide Oren',
        text: 'Stand back a hundred paces. Sight the top. Read the angle. The mountain hands you the third side whether you climb it or not.',
      },
    ],
    objective: { kind: 'complete-lessons', lessonIds: ['trig-ratios-01', 'trig-applications-01'] },
    reward: { xp: 50, gold: 10 },
  },
  {
    id: 'quest-trig-wheel-at-the-summit',
    kingdomId: TRIGONOMETRY,
    kind: 'main',
    order: 1,
    title: 'The Wheel at the Summit',
    story: [
      { text: 'At the summit stands a wheel of exactly one unit radius, and the wind has been turning it for longer than the kingdom has had a name.' },
      {
        speaker: 'Guide Oren',
        text: 'Past ninety degrees a triangle runs out of corners. The wheel never does. Every angle you will ever be asked about is a position on it.',
      },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['unit-circle', 'trig-graphs', 'tangent-function'],
      count: 2,
    },
    reward: { xp: 70, gold: 14 },
  },
  {
    id: 'quest-trig-ridge-of-things-always-true',
    kingdomId: TRIGONOMETRY,
    kind: 'main',
    order: 2,
    title: 'The Ridge of Things Always True',
    story: [
      { text: 'Identity Ridge is a mile of statements that hold for every angle at once, cut into the rock by people who checked them first.' },
      {
        speaker: 'Guide Oren',
        text: 'An identity is true for all of them. An equation is true for a few of them. Confuse the two up here and you will spend the afternoon solving something that was never asking a question.',
      },
    ],
    objective: {
      kind: 'complete-lessons',
      lessonIds: ['trig-identities-01', 'trig-addition-formulas-01', 'half-angle-formulas-01'],
    },
    reward: { xp: 90, gold: 18 },
  },
  {
    id: 'quest-trig-the-traverse',
    kingdomId: TRIGONOMETRY,
    kind: 'main',
    order: 3,
    title: 'The Traverse',
    story: [
      { text: 'The traverse crosses all three camps between dawn and dusk: the basecamp triangle, the summit wheel, and the ridge.' },
      {
        speaker: 'Guide Oren',
        text: 'Two camps I can see and cannot walk between. Give me that distance and I will believe you have done the traverse; the rope is not long enough for the other kind of proof.',
      },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['trig-ratios', 'unit-circle', 'trig-identities', 'trig-equations', 'law-of-sines-cosines'],
      count: 3,
    },
    reward: { xp: 130, gold: 26 },
  },
];

const TRIGONOMETRY_SIDE: QuestDef[] = [
  {
    id: 'quest-trig-basecamp-drill',
    kingdomId: TRIGONOMETRY,
    kind: 'review',
    order: 100,
    title: "Oren's Basecamp Drill",
    story: [
      { text: 'Every party leaving basecamp does the same drill at the same rock before the guide will take them any higher.' },
      {
        speaker: 'Guide Oren',
        text: 'Three sides, three names, and where the angle sits on the wheel. Slow at the rock is only embarrassing. Slow on the ridge is something else.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['trig-ratios', 'unit-circle'], correctCount: 6 },
    reward: { xp: 30, gold: 6 },
  },
  {
    id: 'quest-trig-distance-across-the-corrie',
    kingdomId: TRIGONOMETRY,
    kind: 'side',
    order: 101,
    title: 'The Distance Across the Corrie',
    story: [
      { text: 'The corrie is half a mile of empty air between two paths, and the rope has to be cut to length before anybody sets off.' },
      {
        speaker: 'Guide Oren',
        text: 'Two angles and the side between them, and not a right angle anywhere on the mountain. Bring the other law.',
      },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['law-of-sines-cosines', 'trig-applications'],
      count: 1,
    },
    reward: { xp: 40, gold: 8 },
  },
  {
    id: 'quest-trig-summit-register',
    kingdomId: TRIGONOMETRY,
    kind: 'side',
    order: 102,
    title: 'The Summit Register',
    story: [
      { text: 'The register lives in a tin box under the cairn, and by custom it is signed on the way down rather than on the way up.' },
      {
        speaker: 'Guide Oren',
        text: 'Basecamp, summit, ridge. Write the date beside it. The mountain keeps remarkably good records, and it has all of yours.',
      },
    ],
    objective: { kind: 'clear-quests', questIds: TRIGONOMETRY_MAIN.map((quest) => quest.id) },
    reward: { xp: 40, gold: 8 },
  },
];

/* -------------------------------------------------------------------------- */
/* Calculus Kingdom                                                           */
/* -------------------------------------------------------------------------- */

const CALCULUS = 'kingdom-calculus';

const CALCULUS_MAIN: QuestDef[] = [
  {
    id: 'quest-calculus-gate-you-never-reach',
    kingdomId: CALCULUS,
    kind: 'main',
    order: 0,
    title: 'The Gate You Never Quite Reach',
    story: [
      { text: 'The Limit Gate stands open by an amount too small to measure, and every traveller arrives by getting closer rather than by arriving.' },
      {
        speaker: 'Astronomer Vela',
        text: 'What the function does at the point is one question. What it is heading towards is a different one, and almost everything past this gate is built on the second.',
      },
    ],
    objective: { kind: 'complete-lessons', lessonIds: ['limits-01', 'continuity-01'] },
    reward: { xp: 50, gold: 10 },
  },
  {
    id: 'quest-calculus-how-fast-the-ground',
    kingdomId: CALCULUS,
    kind: 'main',
    order: 1,
    title: 'How Fast the Ground Is Moving',
    story: [
      { text: 'On Derivative Heights the ground is never level, and the survey pegs are cut to report a slope instead of a height.' },
      {
        speaker: 'Astronomer Vela',
        text: 'A cart on this road: where it is, how fast it is going, and whether that is still increasing. Three questions, and each one is the question before it differentiated.',
      },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['derivatives', 'derivative-rules', 'motion-derivatives'],
      count: 2,
    },
    reward: { xp: 80, gold: 16 },
  },
  {
    id: 'quest-calculus-reservoir-below',
    kingdomId: CALCULUS,
    kind: 'main',
    order: 2,
    title: 'The Reservoir Below',
    story: [
      { text: "The Integral Depths hold the kingdom's water. The inflow has been measured every hour for sixty years and the total has never once been worked out." },
      {
        speaker: 'Astronomer Vela',
        text: 'You know the rate at every moment. Add up infinitely many infinitely small moments and out comes a number of litres. The theorem tying those two together is the finest thing in this kingdom.',
      },
    ],
    objective: {
      kind: 'complete-lessons',
      lessonIds: ['antiderivatives-01', 'integrals-01', 'fundamental-theorem-01'],
    },
    reward: { xp: 100, gold: 20 },
  },
  {
    id: 'quest-calculus-peel-then-add',
    kingdomId: CALCULUS,
    kind: 'main',
    order: 3,
    title: 'Peel, Then Add',
    story: [
      { text: 'Not every integral gives its answer up. Most of the work in the depths is making one integral look like a different, easier integral.' },
      {
        speaker: 'Astronomer Vela',
        text: 'Name the inside and the whole thing collapses. Name the wrong inside and you will be down here all night — so choose deliberately, and check that the limits moved with it.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['u-substitution', 'integration-by-parts'], correctCount: 8 },
    reward: { xp: 120, gold: 24 },
  },
  {
    id: 'quest-calculus-sum-with-no-last-term',
    kingdomId: CALCULUS,
    kind: 'main',
    order: 4,
    title: 'The Sum With No Last Term',
    story: [
      { text: 'The observatory is tracking a sum that never ends and settles on a number anyway, and the whole Infinity Realm is the argument about when it is allowed to.' },
      {
        speaker: 'Astronomer Vela',
        text: 'Shrinking terms is not enough — the harmonic series shrinks and still runs away to infinity. Show me it converges, how fast, and what function it adds up to.',
      },
      { text: 'The last of the kingdom, in one night: a limit, a derivative, an integral and a series.' },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: [
        'limits',
        'derivative-rules',
        'integrals',
        'fundamental-theorem',
        'series-convergence',
        'taylor-series',
      ],
      count: 4,
    },
    reward: { xp: 150, gold: 30 },
  },
];

const CALCULUS_SIDE: QuestDef[] = [
  {
    id: 'quest-calculus-night-watch',
    kingdomId: CALCULUS,
    kind: 'review',
    order: 100,
    title: "Vela's Night Watch",
    story: [
      { text: 'The observatory takes the same readings every clear night, an hour apart, and the interesting number is never the reading itself.' },
      {
        speaker: 'Astronomer Vela',
        text: 'The average rate between two readings, and then the reading as the gap closes to nothing. That is the whole watch, and it is very nearly the whole subject.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['limits', 'rates-of-change'], correctCount: 6 },
    reward: { xp: 30, gold: 6 },
  },
  {
    id: 'quest-calculus-weight-of-the-bell',
    kingdomId: CALCULUS,
    kind: 'side',
    order: 101,
    title: 'The Weight of the Bell',
    story: [
      { text: 'The observatory bell is being rehung, and the founder wants to know where it balances and what it will cost to raise.' },
      {
        speaker: 'Astronomer Vela',
        text: 'A force that changes as you lift, and a shape whose weight is not in the middle of it. Both are the same trick: cut it into slices and add the slices up.',
      },
    ],
    objective: { kind: 'master-concepts', conceptIds: ['work-integrals', 'center-of-mass'], count: 1 },
    reward: { xp: 40, gold: 8 },
  },
  {
    id: 'quest-calculus-observatory-ledger',
    kingdomId: CALCULUS,
    kind: 'side',
    order: 102,
    title: 'The Observatory Ledger',
    story: [
      { text: 'The ledger records everyone who has stood the whole watch: the gate, the heights, the depths and the realm past them.' },
      {
        speaker: 'Astronomer Vela',
        text: 'There is no road out of this kingdom yet. That is not a wall — it is only where the map we have drawn so far runs out of paper.',
      },
    ],
    objective: { kind: 'clear-quests', questIds: CALCULUS_MAIN.map((quest) => quest.id) },
    reward: { xp: 45, gold: 9 },
  },
];

/* -------------------------------------------------------------------------- */
/* Statistics Kingdom                                                         */
/* -------------------------------------------------------------------------- */

const STATISTICS = 'kingdom-statistics';

const STATISTICS_MAIN: QuestDef[] = [
  {
    id: 'quest-statistics-crowd-and-the-question',
    kingdomId: STATISTICS,
    kind: 'main',
    order: 0,
    title: 'The Crowd and the Question',
    story: [
      { text: 'The Statistics Kingdom counts everything it owns and believes almost none of it until somebody has drawn the picture.' },
      {
        speaker: 'Registrar Juno',
        text: 'Forty households, and the mayor wants one number for the lot of them. Which one — and be ready to tell him why the other two would have flattered him.',
      },
      { text: 'A mean, a median and a mode are three answers to one question, and the picture is what shows you which of them is lying.' },
    ],
    objective: { kind: 'complete-lessons', lessonIds: ['data-displays-01', 'mean-median-mode-01'] },
    reward: { xp: 50, gold: 10 },
  },
  {
    id: 'quest-statistics-fen-of-maybe',
    kingdomId: STATISTICS,
    kind: 'main',
    order: 1,
    title: 'The Fen of Maybe',
    story: [
      { text: 'Chance Fen is marshland where nothing is certain and everything can still be counted before it happens.' },
      {
        speaker: 'Registrar Juno',
        text: 'The ferryman says his crossing is safe four days in five, and he has never once written down which four. Count the ways it can go and the boast turns into a number we can argue with.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['basic-probability', 'compound-probability'], correctCount: 6 },
    reward: { xp: 70, gold: 14 },
  },
  {
    id: 'quest-statistics-everything-that-could-happen',
    kingdomId: STATISTICS,
    kind: 'main',
    order: 2,
    title: 'Everything That Could Happen',
    story: [
      { text: 'On Sample Space Shore they write out everything that could happen before anything does, on a board a hundred feet long.' },
      {
        speaker: 'Registrar Juno',
        text: 'Then somebody tells you the first coin came down heads and half the board is struck through. What is left is not what you started with, and that difference is where every gambler in the fen loses their coat.',
      },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['sample-spaces', 'probability-events', 'conditional-probability'],
      count: 2,
    },
    reward: { xp: 90, gold: 18 },
  },
  {
    id: 'quest-statistics-machine-that-rolls',
    kingdomId: STATISTICS,
    kind: 'main',
    order: 3,
    title: 'The Machine That Rolls',
    story: [
      { text: 'The Random Variable Works build machines whose output is settled by chance, and sell them on the number they produce on average.' },
      {
        speaker: 'Registrar Juno',
        text: 'Nobody buys a machine on one roll of it. They buy it on what it pays over ten thousand — and on how far from that it is willing to wander on a bad afternoon.',
      },
    ],
    objective: {
      kind: 'complete-lessons',
      lessonIds: ['discrete-random-variables-01', 'expected-value-01', 'binomial-distribution-01'],
    },
    reward: { xp: 100, gold: 20 },
  },
  {
    id: 'quest-statistics-what-the-sample-may-say',
    kingdomId: STATISTICS,
    kind: 'main',
    order: 4,
    title: 'What the Sample Is Entitled to Say',
    story: [
      { text: 'The Inference Court sits twice a week and hears one kind of case: somebody has measured a few and would like to speak about all.' },
      {
        speaker: 'Registrar Juno',
        text: 'Two hundred households out of ninety thousand. You may say something. You may not say it flatly — and the interval is how this court makes you say it honestly.',
      },
      { text: 'The whole kingdom in one hearing: a summary, a probability, a distribution, and the sample standing in for everyone who was not asked.' },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: [
        'mean-median-mode',
        'basic-probability',
        'conditional-probability',
        'expected-value',
        'sampling-distributions',
        'confidence-intervals',
      ],
      count: 4,
    },
    reward: { xp: 150, gold: 30 },
  },
];

const STATISTICS_SIDE: QuestDef[] = [
  {
    id: 'quest-statistics-census-round',
    kingdomId: STATISTICS,
    kind: 'review',
    order: 100,
    title: "Juno's Census Round",
    story: [
      { text: 'The census is taken every quarter with the same forty questions, and the answers only mean anything held up against last quarter’s.' },
      {
        speaker: 'Registrar Juno',
        text: 'Summarise it, then say how likely. Two habits, and everything else this kingdom does is built on top of them.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['mean-median-mode', 'basic-probability'], correctCount: 6 },
    reward: { xp: 30, gold: 6 },
  },
  {
    id: 'quest-statistics-almanac-and-weathervane',
    kingdomId: STATISTICS,
    kind: 'side',
    order: 101,
    title: 'The Almanac and the Weathervane',
    story: [
      { text: 'The almanac has noticed that the years with the most weddings are the years with the most storms, and has started giving advice about it.' },
      {
        speaker: 'Registrar Juno',
        text: 'A line through the dots is a good line. It is not a reason. Fit it, predict with it, and then say out loud the thing it does not prove.',
      },
    ],
    objective: { kind: 'master-concepts', conceptIds: ['correlation', 'regression'], count: 1 },
    reward: { xp: 40, gold: 8 },
  },
  {
    id: 'quest-statistics-register-of-the-court',
    kingdomId: STATISTICS,
    kind: 'side',
    order: 102,
    title: 'The Register of the Court',
    story: [
      { text: 'The Court keeps a register of everyone entitled to speak in it, and entitlement here is a matter of evidence rather than of rank.' },
      {
        speaker: 'Registrar Juno',
        text: 'Observatory, fen, shore, works, court. You have been round all five and you have not once claimed more than the numbers allowed. Sign it.',
      },
    ],
    objective: { kind: 'clear-quests', questIds: STATISTICS_MAIN.map((quest) => quest.id) },
    reward: { xp: 45, gold: 9 },
  },
];

/* -------------------------------------------------------------------------- */
/* Linear Algebra Kingdom                                                     */
/* -------------------------------------------------------------------------- */

const LINEAR_ALGEBRA = 'kingdom-linear-algebra';

const LINEAR_ALGEBRA_MAIN: QuestDef[] = [
  {
    id: 'quest-linear-algebra-two-arrows',
    kingdomId: LINEAR_ALGEBRA,
    kind: 'main',
    order: 0,
    title: 'Everywhere Two Arrows Can Reach',
    story: [
      { text: 'Vector Harbour deals in arrows: a length and a direction, and nothing else about one matters — including where it happens to be standing.' },
      {
        speaker: 'Quartermaster Bex',
        text: 'Two tugs on one barge, pulling at an angle to each other. I do not want a drawing of it. I want the single arrow that would have done the same job.',
      },
    ],
    objective: { kind: 'complete-lessons', lessonIds: ['vectors-01', 'linear-combinations-01'] },
    reward: { xp: 50, gold: 10 },
  },
  {
    id: 'quest-linear-algebra-grid-on-the-anvil',
    kingdomId: LINEAR_ALGEBRA,
    kind: 'main',
    order: 1,
    title: 'The Grid on the Anvil',
    story: [
      { text: 'The Matrix Foundry hammers a grid of numbers row by row until whatever it was hiding is lying on the surface.' },
      {
        speaker: 'Quartermaster Bex',
        text: 'Swap a row, scale a row, add one row to another. Three moves, and not one of them changes the answer. That is the entire trade and it takes a week to believe.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['matrices', 'row-reduction'], correctCount: 6 },
    reward: { xp: 70, gold: 14 },
  },
  {
    id: 'quest-linear-algebra-one-none-endlessly-many',
    kingdomId: LINEAR_ALGEBRA,
    kind: 'main',
    order: 2,
    title: 'One, None, or Endlessly Many',
    story: [
      { text: 'The System Halls hear many equations at once, and in nine hundred years they have returned only three verdicts.' },
      {
        speaker: 'Quartermaster Bex',
        text: 'Exactly one, none at all, or a whole line of them. Never a fourth answer — and the shape of the reduced grid tells you which it will be before you solve a thing.',
      },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['linear-systems', 'rank', 'solution-sets'],
      count: 2,
    },
    reward: { xp: 90, gold: 18 },
  },
  {
    id: 'quest-linear-algebra-rules-without-the-arrow',
    kingdomId: LINEAR_ALGEBRA,
    kind: 'main',
    order: 3,
    title: 'The Rules Without the Arrow',
    story: [
      { text: 'The Archives keep the rules an arrow obeyed, filed separately from the arrow — and then hand them to things that are not arrows at all.' },
      {
        speaker: 'Quartermaster Bex',
        text: 'Polynomials. Functions. Grids of other grids. If it adds and scales the way an arrow does, everything we learned at the harbour still holds, and nobody has to draw anything.',
      },
    ],
    objective: {
      kind: 'complete-lessons',
      lessonIds: ['vector-spaces-01', 'basis-01', 'linear-transformations-01'],
    },
    reward: { xp: 100, gold: 20 },
  },
  {
    id: 'quest-linear-algebra-directions-that-will-not-turn',
    kingdomId: LINEAR_ALGEBRA,
    kind: 'main',
    order: 4,
    title: 'The Directions That Will Not Turn',
    story: [
      { text: 'At the top of the Eigen Spire there is only one question left worth asking: is there a direction this transformation refuses to turn?' },
      {
        speaker: 'Quartermaster Bex',
        text: 'Stretch it, squash it, flip it — but do not turn it. Find those directions and the whole tangled grid comes apart into a handful of numbers on a diagonal.',
      },
      { text: 'The harbour, the foundry, the halls and the archives, all wanted at once and none of them optional.' },
    ],
    objective: {
      kind: 'master-concepts',
      conceptIds: ['vectors', 'matrices', 'linear-systems', 'basis', 'eigenvalues', 'eigenvectors'],
      count: 4,
    },
    reward: { xp: 150, gold: 30 },
  },
];

const LINEAR_ALGEBRA_SIDE: QuestDef[] = [
  {
    id: 'quest-linear-algebra-harbour-drill',
    kingdomId: LINEAR_ALGEBRA,
    kind: 'review',
    order: 100,
    title: "Bex's Harbour Drill",
    story: [
      { text: 'Every crew on the harbour runs the same drill on the same two arrows before it is trusted with a barge.' },
      {
        speaker: 'Quartermaster Bex',
        text: 'Add them, scale them, and then tell me the angle between them without measuring it. The dot product is the whole answer and it fits on one line.',
      },
    ],
    objective: { kind: 'practice', conceptIds: ['vectors', 'dot-product'], correctCount: 6 },
    reward: { xp: 30, gold: 6 },
  },
  {
    id: 'quest-linear-algebra-bill-of-lading',
    kingdomId: LINEAR_ALGEBRA,
    kind: 'side',
    order: 101,
    title: 'The Bill of Lading',
    story: [
      { text: 'A bill of lading has to be readable backwards: what went into the hold, worked out from what came off it.' },
      {
        speaker: 'Quartermaster Bex',
        text: 'One number tells you whether the loading can be undone at all. If it comes out zero, no clerk in this kingdom can reverse it and you had better still have the original.',
      },
    ],
    objective: { kind: 'master-concepts', conceptIds: ['determinants', 'matrix-inverse'], count: 1 },
    reward: { xp: 40, gold: 8 },
  },
  {
    id: 'quest-linear-algebra-spire-ledger',
    kingdomId: LINEAR_ALGEBRA,
    kind: 'side',
    order: 102,
    title: 'The Spire Ledger',
    story: [
      { text: 'The ledger at the top of the spire is a short one: harbour, foundry, halls, archives, and whoever has climbed the whole lot.' },
      {
        speaker: 'Quartermaster Bex',
        text: 'Nothing up here leans on a limit or a derivative and it never did. You came by the other road entirely and arrived at the same height.',
      },
    ],
    objective: { kind: 'clear-quests', questIds: LINEAR_ALGEBRA_MAIN.map((quest) => quest.id) },
    reward: { xp: 45, gold: 9 },
  },
];

/**
 * Every quest in the world.
 *
 * All ten kingdoms of the MVP map now have a story. That is a milestone and not
 * a rule: a kingdom added tomorrow starts without one, and the map, the
 * lessons and the practice inside it work perfectly well in the meantime —
 * `QuestBoard` renders the empty board on purpose.
 */
export const QUESTS: QuestDef[] = [
  ...VILLAGE_MAIN,
  ...VILLAGE_SIDE,
  ...NUMBER_MAIN,
  ...NUMBER_SIDE,
  ...FRACTION_MAIN,
  ...FRACTION_SIDE,
  ...ALGEBRA_MAIN,
  ...ALGEBRA_SIDE,
  ...GEOMETRY_MAIN,
  ...GEOMETRY_SIDE,
  ...FUNCTION_MAIN,
  ...FUNCTION_SIDE,
  ...TRIGONOMETRY_MAIN,
  ...TRIGONOMETRY_SIDE,
  ...CALCULUS_MAIN,
  ...CALCULUS_SIDE,
  ...STATISTICS_MAIN,
  ...STATISTICS_SIDE,
  ...LINEAR_ALGEBRA_MAIN,
  ...LINEAR_ALGEBRA_SIDE,
];
